import type { Control, FieldValues, Path } from 'react-hook-form';

import { Link } from '@tanstack/react-router';

import type { DataQuality } from '../types';

import { DataQualitySelect } from './DataQualitySelect';

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { useTranslation } from 'react-i18next';

const DATA_QUALITY_VALUES: readonly DataQuality[] = [
  'measured',
  'calculated',
  'estimated',
  'spend_based',
];

function toStringValue(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function isDataQuality(value: unknown): value is DataQuality {
  return DATA_QUALITY_VALUES.includes(value as DataQuality);
}

interface SelectOption {
  value: string;
  label: string;
}

interface ReportingPeriodFieldProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>;
  options: SelectOption[];
  disabled: boolean;
  description: string;
  /** Optional callback when the value changes (used to sync with page-level period). */
  onValueChange?: (value: string) => void;
}

export function ReportingPeriodField<TFieldValues extends FieldValues>({
  control,
  options,
  disabled,
  description,
  onValueChange,
}: ReportingPeriodFieldProps<TFieldValues>) {
  const { t } = useTranslation('ghg');
  return (
    <FormField
      control={control}
      name={'reportingPeriodId' as Path<TFieldValues>}
      render={({ field }) => (
        <FormItem>
          <FormLabel>Reporting Period</FormLabel>
          <FormDescription>{description}</FormDescription>
          <FormControl>
            <Select
              options={options}
              value={toStringValue(field.value)}
              onChange={(value) => {
                field.onChange(value);
                onValueChange?.(value);
              }}
              placeholder={t('placeholder.selectAReportingPeriod')}
              disabled={disabled}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

interface FacilityFieldProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>;
  options: SelectOption[];
  disabled: boolean;
  label: string;
  description: string;
}

export function FacilityField<TFieldValues extends FieldValues>({
  control,
  options,
  disabled,
  label,
  description,
}: FacilityFieldProps<TFieldValues>) {
  const { t } = useTranslation('ghg');
  return (
    <FormField
      control={control}
      name={'facilityId' as Path<TFieldValues>}
      render={({ field }) => (
        <FormItem>
          <div className="flex items-center justify-between">
            <FormLabel>{label}</FormLabel>
            <Link
              to="/settings/organisation-structure"
              className="text-xs text-primary hover:underline"
              target="_blank"
            >
              Manage facilities
            </Link>
          </div>
          <FormDescription>{description}</FormDescription>
          <FormControl>
            <Select
              options={options}
              value={toStringValue(field.value)}
              onChange={field.onChange}
              placeholder={t('placeholder.selectAFacility')}
              disabled={disabled}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

interface QuantityFieldProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>;
  disabled: boolean;
  placeholder: string;
}

export function QuantityField<TFieldValues extends FieldValues>({
  control,
  disabled,
  placeholder,
}: QuantityFieldProps<TFieldValues>) {
  return (
    <FormField
      control={control}
      name={'quantity' as Path<TFieldValues>}
      render={({ field }) => (
        <FormItem>
          <FormLabel>Quantity</FormLabel>
          <FormControl>
            <Input
              type="number"
              step="any"
              min="0"
              placeholder={placeholder}
              disabled={disabled}
              name={field.name}
              value={toStringValue(field.value)}
              onChange={field.onChange}
              onBlur={field.onBlur}
              ref={field.ref}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

interface UnitInputFieldProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>;
  disabled: boolean;
  placeholder: string;
}

export function UnitInputField<TFieldValues extends FieldValues>({
  control,
  disabled,
  placeholder,
}: UnitInputFieldProps<TFieldValues>) {
  return (
    <FormField
      control={control}
      name={'unit' as Path<TFieldValues>}
      render={({ field }) => (
        <FormItem>
          <FormLabel>Unit</FormLabel>
          <FormControl>
            <Input
              placeholder={placeholder}
              disabled={disabled}
              name={field.name}
              value={toStringValue(field.value)}
              onChange={field.onChange}
              onBlur={field.onBlur}
              ref={field.ref}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

interface UnitSelectFieldProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>;
  disabled: boolean;
  options: SelectOption[];
}

export function UnitSelectField<TFieldValues extends FieldValues>({
  control,
  disabled,
  options,
}: UnitSelectFieldProps<TFieldValues>) {
  const { t } = useTranslation('ghg');
  return (
    <FormField
      control={control}
      name={'unit' as Path<TFieldValues>}
      render={({ field }) => (
        <FormItem>
          <FormLabel>Unit</FormLabel>
          <FormControl>
            <Select
              options={options}
              value={toStringValue(field.value)}
              onChange={field.onChange}
              placeholder={t('placeholder.selectUnit4')}
              disabled={disabled}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

interface DataQualityFieldProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>;
  disabled: boolean;
  allowedValues?: DataQuality[];
}

export function DataQualityField<TFieldValues extends FieldValues>({
  control,
  disabled,
  allowedValues,
}: DataQualityFieldProps<TFieldValues>) {
  return (
    <FormField
      control={control}
      name={'dataQuality' as Path<TFieldValues>}
      render={({ field }) => (
        <FormItem>
          <FormLabel>Data Quality</FormLabel>
          <FormDescription>
            Select the data quality tier per GHG Protocol guidelines.
          </FormDescription>
          <FormControl>
            <DataQualitySelect
              value={isDataQuality(field.value) ? field.value : 'estimated'}
              onChange={field.onChange}
              disabled={disabled}
              allowedValues={allowedValues}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
